import {
    OfficialReportRequestBody,
    OfficialReportStatus,
    OfficialReportType,
    ReportHeadway,
} from '@/types/officialReport'
import { Log } from '@/types/logs'
import { FormValuesType } from '../schema'
import { OFFICIAL_REPORT_FORM_FIELDS } from '../constant'
import { isLateOfficialReportType, isHeadwayOfficialReportType, isGenericOfficialReportType } from './checkReportType'
import { isHeadwayValid } from './isHeadwayValid'
import { getFormattedDate } from '@/helpers/time'
import { formatDetailsBeforeSave } from './formatDetailsBeforeSave'
import { buildDescription } from './generateDetails'

export const buildOfficialReportRequestBody = ({
    type,
    status,
    log,
    values,
}: {
    type: OfficialReportType
    status: OfficialReportStatus
    log: Log
    values: FormValuesType
}): OfficialReportRequestBody => {
    const driverStatement = values[OFFICIAL_REPORT_FORM_FIELDS.driverStatement] || ''

    const description = isGenericOfficialReportType(type)
        ? values[OFFICIAL_REPORT_FORM_FIELDS.details] || ''
        : buildDescription(type, log, values)

    const requestBody: OfficialReportRequestBody = {
        id: log.report?.id || crypto.randomUUID(),
        incidentDate: getFormattedDate({
            date: values[OFFICIAL_REPORT_FORM_FIELDS.dateOfIssue],
            time: values[OFFICIAL_REPORT_FORM_FIELDS.timeOfIssue],
        }),
        status,
        subcategory: type,
        logId: log.id,
        driverBadgeNumber: values[OFFICIAL_REPORT_FORM_FIELDS.driverEmployeeNumber] || '',
        issueLocation: values[OFFICIAL_REPORT_FORM_FIELDS.locationOfIssue] || '',
        description,
        driverStatement,
    }

    if (isLateOfficialReportType(type)) {
        requestBody.takeOverTimeMode = values[OFFICIAL_REPORT_FORM_FIELDS.timeType]
        requestBody.takeOverDate = getFormattedDate({
            date: values[OFFICIAL_REPORT_FORM_FIELDS.dateOfIssue],
            time: values[OFFICIAL_REPORT_FORM_FIELDS.takeoverTime],
        })
        requestBody.actualTakeOverDate = getFormattedDate({
            date: values[OFFICIAL_REPORT_FORM_FIELDS.dateOfIssue],
            time: values[OFFICIAL_REPORT_FORM_FIELDS.actualTakeoverTime],
        })
    }

    if (isHeadwayOfficialReportType(type)) {
        const formattedHeadways = values[OFFICIAL_REPORT_FORM_FIELDS.headways].reduce<Omit<ReportHeadway, 'id'>[]>(
            (acc, item) =>
                isHeadwayValid(item)
                    ? [
                          ...acc,
                          {
                              headway: +Number(item.headway).toFixed(2),
                              busStop: item.busStop as string,
                              observedAt: getFormattedDate({
                                  date: values[OFFICIAL_REPORT_FORM_FIELDS.dateOfIssue],
                                  time: item.observedAt,
                              }),
                          },
                      ]
                    : acc,
            []
        )

        requestBody.headways = formattedHeadways
    }

    // Generic reports use the free-text DetailsEditor (which produces HTML) — strip it on submit
    if (requestBody.status === OfficialReportStatus.Submitted && isGenericOfficialReportType(type)) {
        requestBody.description = formatDetailsBeforeSave(requestBody.description)
    }

    if (requestBody.subcategory === OfficialReportType.LateDeparture) {
        requestBody.subcategory = OfficialReportType.LateTakeover
    }

    return requestBody
}
